import {
  PreRegistrationCompanyForm,
  PreRegistrationCmaForm,
  PreRegistrationContactsForm,
  PreRegistrationNdaForm,
  PreRegistrationForm,
  ContactPerson,
  PreRegistrationStepKey,
} from '@common/interfaces/pre-registration/pre-registration.interface';
import { NDA_ACCEPTED_VALUE } from './pre-registration.const';

/**
 * Maps the internal (camelCase) reactive-form value onto the backend's
 * snake_case bracketed contract. Keeping this translation in one place lets the
 * UI keep readable control names while `buildFormData` produces the exact keys
 * the API expects (e.g. `company_info[company_name][en]`, `nda[signatory_name]`).
 *
 * File values are passed through untouched; `buildFormData` appends them as raw
 * multipart parts and skips `null`/`undefined` (so the optional logo is omitted
 * when empty).
 */

function mapCompanyInfo(
  v: PreRegistrationCompanyForm
): Record<string, unknown> {
  return {
    investee_type: v.investeeType,
    company_name: { en: v.companyName.en, ar: v.companyName.ar },
    cr_number: v.crNumber,
    cr_expiry_date: v.crExpiryDate,
    cr_certificate: v.crFile,
    aoa: v.aoaFile,
    logo: v.companyLogo,
  };
}

function mapCmaLicensing(v: PreRegistrationCmaForm): Record<string, unknown> {
  return {
    cma_license_number: v.cmaLicenseNumber,
    cma_license_file: v.cmaLicenseCopy,
  };
}

/**
 * Normalizes the spaced, 9-digit Saudi number from lib-phone-input into the
 * backend's expected `+966XXXXXXXXX` form (strips spaces, adds the dial code).
 */
function normalizeSaudiMobile(mobile: string): string {
  const digits = (mobile ?? '').replace(/\D/g, '');
  return digits ? `+966${digits}` : '';
}

function mapPerson(p: ContactPerson): Record<string, unknown> {
  return {
    name: p.name,
    position: p.position,
    mobile: normalizeSaudiMobile(p.mobile),
    email: p.email,
  };
}

function mapContacts(v: PreRegistrationContactsForm): Record<string, unknown> {
  return {
    investee: mapPerson(v.contactPerson),
    investee_delegate: mapPerson(v.authorizedPerson),
  };
}

function mapNda(v: PreRegistrationNdaForm): Record<string, unknown> {
  return {
    agreed: v.accepted.includes(NDA_ACCEPTED_VALUE),
    signatory_name: v.signatoryName,
  };
}

/**
 * Maps a single step's raw group value onto its API sub-object, keyed by the
 * backend group name — the shape the `/validate` endpoint receives per step.
 * Returns `{}` for the review step (nothing to validate server-side).
 */
export function mapStepPayload(
  stepKey: PreRegistrationStepKey,
  groupValue: unknown
): Record<string, unknown> {
  switch (stepKey) {
    case 'company':
      return {
        company_info: mapCompanyInfo(groupValue as PreRegistrationCompanyForm),
      };
    case 'cma':
      return {
        cma_licensing: mapCmaLicensing(groupValue as PreRegistrationCmaForm),
      };
    case 'contacts':
      return {
        contacts: mapContacts(groupValue as PreRegistrationContactsForm),
      };
    case 'nda':
      return { nda: mapNda(groupValue as PreRegistrationNdaForm) };
    default:
      return {};
  }
}

/**
 * Reverse of the forward mapping above: backend key segment → reactive-form
 * control name. Used to translate server validation error keys (e.g.
 * `contacts.investee.mobile`) back onto form controls (`contactsForm
 * .contactPerson.mobile`) so the message lands on the right field instead of a
 * generic toast. Keep in sync with the `map*` functions.
 */
const BACKEND_TO_FORM_KEY: Record<string, string> = {
  // Top-level step groups
  company_info: 'companyForm',
  cma_licensing: 'cmaForm',
  contacts: 'contactsForm',
  nda: 'ndaForm',
  // Company info fields
  investee_type: 'investeeType',
  company_name: 'companyName',
  cr_number: 'crNumber',
  cr_expiry_date: 'crExpiryDate',
  cr_certificate: 'crFile',
  aoa: 'aoaFile',
  logo: 'companyLogo',
  // CMA licensing fields
  cma_license_number: 'cmaLicenseNumber',
  cma_license_file: 'cmaLicenseCopy',
  // Contacts (person groups + shared person fields)
  investee: 'contactPerson',
  investee_delegate: 'authorizedPerson',
  // NDA fields
  agreed: 'accepted',
  signatory_name: 'signatoryName',
};

/** Converts a snake_case segment to camelCase (e.g. `cr_number` → `crNumber`). */
function snakeToCamel(segment: string): string {
  return segment.replace(/_([a-z0-9])/g, (_, char: string) =>
    char.toUpperCase()
  );
}

/**
 * Translates a map of backend validation error keys onto reactive-form control
 * paths so every field's error lands inline on its input (not in a toast).
 * Applied segment by segment: explicit renames win, then any remaining
 * snake_case segment is camelCased, and already-matching leaves (`mobile`,
 * `email`, `en`, `ar`, …) pass through unchanged.
 */
export function mapApiErrorKeysToForm(
  errors: Record<string, string[]>
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const [key, messages] of Object.entries(errors)) {
    const mappedKey = key
      .split('.')
      .map(segment => BACKEND_TO_FORM_KEY[segment] ?? snakeToCamel(segment))
      .join('.');
    result[mappedKey] = messages;
  }
  return result;
}

/** Maps the whole form value onto the full submit contract. */
export function mapFullPayload(
  value: PreRegistrationForm
): Record<string, unknown> {
  return {
    company_info: mapCompanyInfo(value.companyForm),
    cma_licensing: mapCmaLicensing(value.cmaForm),
    contacts: mapContacts(value.contactsForm),
    nda: mapNda(value.ndaForm),
  };
}
